import React, { FunctionComponent, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Trash2 as TrashIcon } from "react-feather";
import { toastr } from "react-redux-toastr";

import moment from 'moment';

import CrudList, { CrudTableState, initialTableState } from "../../components/crud/CrudList";
import { CrudTableColumnBuilder } from "../../components/crud/CrudTableColumnBuilder";

import { useApiService } from "../../hooks/useApiService";
import { useEffectOnMount } from "../../hooks/useEffectOnMount";

import ConfirmationModal from "../../components/ConfirmationModal";

import { Publisher } from "../../domain/Publisher";

type PublisherTableRow = {
  id: number;
  email: string;
  name: string;
  country: string;
  status: string;
  verified: string;
  apps: number;
  created_at: string;
};

const PublishersListPage: FunctionComponent = () => {

  const [publishers, setPublishers] = useState<PublisherTableRow[]>([]);
  const [tableState, setTableState] = useState<CrudTableState<PublisherTableRow>>(initialTableState);

  const deletePublisherModalRef = useRef<ConfirmationModal>(null);

  const columns = CrudTableColumnBuilder(tableState)
    .addIndexColumn()
    .addColumn({ text: 'Email', dataField: 'email', sort: true })
    .addColumn({ text: 'Name', dataField: 'name', sort: true })
    .addColumn({ text: 'Country', dataField: 'country', headerStyle: { width: '90px' } })
    .addColumn({ text: 'Status', dataField: 'status', headerStyle: { width: '110px' } })
    .addColumn({ text: 'Verified', dataField: 'verified', headerStyle: { width: '100px' } })
    .addColumn({ text: 'Apps', dataField: 'apps', headerStyle: { width: '80px' } })
    .addColumn({ text: 'Signed up', dataField: 'created_at', headerStyle: { width: '220px' } })
    .addActionsColumn((row: PublisherTableRow) => (
      <div>
        <Link to="#" onClick={() => deletePublisherModalRef.current!.open(row)}>
          <TrashIcon size={18} className="ml-1"></TrashIcon>
        </Link>
      </div>
    ))
    .getColumns();

  const mapPublisherToRow = (p: Publisher): PublisherTableRow => ({
    id: p.id,
    email: p.email,
    name: p.name,
    country: p.country || '—',
    status: p.status,
    verified: p.email_verified ? 'Yes' : 'No',
    apps: p.apps,
    created_at: moment(p.created_at).format('MMMM Do YYYY, h:mm:ss a'),
  });

  // The endpoint returns every account at once (the beta has a small number), so filtering/sorting is done in the
  // browser and there is no server pagination.
  const [fetchPublishers, { isLoading: isFetchingPublishers }] = useApiService('publisher', publisherService => (
    (nextTableState: CrudTableState<PublisherTableRow>) => (
      publisherService.fetchPublishers().then(rows => {
        const mapped = rows.map(mapPublisherToRow);
        const filter = (nextTableState.filter || '').toString().toLowerCase();
        const visible = filter
          ? mapped.filter(r => r.email.toLowerCase().includes(filter) || r.name.toLowerCase().includes(filter))
          : mapped;
        setPublishers(visible);
        setTableState({ ...nextTableState, totalSize: visible.length });
      })
    )
  ));

  const onTableChange = (nextTableState: CrudTableState<PublisherTableRow>) => fetchPublishers(nextTableState);
  useEffectOnMount(() => fetchPublishers(tableState));

  const [deletePublisher, { isLoading: isDeletingPublisher }] = useApiService('publisher', publisherService => (
    (row: PublisherTableRow) => (
      publisherService.deletePublisher(row.id).then(() => {
        toastr.success('Success', 'Developer account deleted');
        deletePublisherModalRef.current!.hide();
        fetchPublishers(tableState);
      })
    )
  ));

  return (
    <React.Fragment>
      <CrudList
        title="Developers (publishers)"
        tableData={publishers}
        tableColumns={columns}
        tableIsLoading={isFetchingPublishers}
        onTableChange={onTableChange}
        tableState={tableState}
        useSimpleFilter
        simpleFilterPlaceholder="Filter by email or name..."
      />

      <ConfirmationModal
        ref={deletePublisherModalRef}
        title="Delete developer account"
        modalBody={(row: PublisherTableRow) => (
          `Permanently delete "${row?.email}"? Their ${row?.apps || 0} app(s) will be removed from WeShareApps. This cannot be undone.`
        )}
        confirmButtonColor="danger"
        confirmButtonDisabled={isDeletingPublisher}
        confirmButtonLabel={isDeletingPublisher ? 'Deleting...' : 'Delete account'}
        cancelButtonDisabled={isDeletingPublisher}
        onConfirm={(row: PublisherTableRow) => deletePublisher(row)}
      />
    </React.Fragment>
  );
};

export default PublishersListPage;
